/**
 * Confirmation dialog that requires a reason before a moderation action (requirements 1.10, 3.8, 6.6).
 */
import { useEffect, useState } from 'react';
import type { ModerationReasonCategory } from '../../lib/types';
import { REASON_CATEGORY_LABELS } from './moderationLabels';

export interface ReasonDialogResult {
  reason_category: ModerationReasonCategory;
  reason: string;
}

interface ReasonDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  /** Lifts and suspensions only need a reason text, not a category. */
  withCategory?: boolean;
  onCancel: () => void;
  onConfirm: (result: ReasonDialogResult) => Promise<void>;
}

const CATEGORIES = Object.keys(REASON_CATEGORY_LABELS) as ModerationReasonCategory[];

export default function ReasonDialog({
  open, title, description, confirmLabel, withCategory = true, onCancel, onConfirm,
}: ReasonDialogProps) {
  const [category, setCategory] = useState<ModerationReasonCategory>('prohibited_content');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCategory('prohibited_content');
      setReason('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!reason.trim()) {
      setError('理由を入力してください');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({ reason_category: category, reason: reason.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作に失敗しました');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-label={title}>
      <form onSubmit={handleSubmit} className="w-full max-w-lg bg-white p-6 shadow-xl text-left">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        {description && <p className="mt-2 text-sm text-gray-600">{description}</p>}
        {withCategory && (
          <label className="mt-4 block text-sm font-semibold text-gray-700">
            理由の区分
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value as ModerationReasonCategory)}
              className="mt-1 block w-full border border-gray-300 px-3 py-2 text-sm"
            >
              {CATEGORIES.map((value) => (
                <option key={value} value={value}>{REASON_CATEGORY_LABELS[value]}</option>
              ))}
            </select>
          </label>
        )}
        <label className="mt-4 block text-sm font-semibold text-gray-700">
          理由（記録に残ります）
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={1000}
            rows={3}
            className="mt-1 block w-full border border-gray-300 px-3 py-2 text-sm"
            placeholder="確認した内容と判断の理由"
          />
        </label>
        {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onCancel} disabled={submitting} className="border border-gray-300 px-4 py-2 text-sm">
            キャンセル
          </button>
          <button type="submit" disabled={submitting} className="bg-red-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {submitting ? '実行中...' : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
